export interface ApiErrorResponse {
  error: true;
  error_type: string;
  message: string;
}

export interface CampsiteType {
  id: number;
  name: string;
  slug: string;
}

export interface Season {
  id: number;
  name: string;
  is_lock: boolean;
  is_deleted: boolean;
  description: string | null;
  time_periods: TimePeriod[];
}

export interface TimePeriod {
  date_start: string;
  date_end: string;
}

export interface MinimumNight {
  campsite_type: string;
  amount: number;
}

export interface Person {
  id: number;
  code: string;
  external_id: string | null;
  name: string;
  person_type: "adult" | "child" | "pet";
  description: string;
  prices: PersonPrice[];
  age_from: number;
  age_to: number;
}

export interface PersonPrice {
  value: number;
  season: number;
  campsite_type: string;
}

export interface Extra {
  applies_for_accommodation: boolean;
  applies_for_camper: boolean;
  applies_for_camping: boolean;
  applies_for_caravan: boolean;
  applies_for_tent: boolean;
  billing_type: string;
  can_be_free: boolean;
  code: string;
  description: string;
  external_id: string | null;
  extra_sub_type: string;
  extra_type: string;
  id: number;
  name: string;
  price: number;
  tax: number | null;
  transit: boolean;
}

export interface ExtraFields {
  is_mandatory?: boolean;
  billing_type?: string;
  extra_type?: string;
  extra_sub_type?: string;
  person_amount?: number;
}

export interface ExtraCurrencyAlt {
  price: number;
  currency: string;
}

export interface TopAttribute {
  name: string;
  icon_svg: string | null;
}

export const INPUT_FIELD_MODE = {
  Optional: "o",
  Mandatory: "m",
  No: "n",
} as const;

export type InputFieldMode = (typeof INPUT_FIELD_MODE)[keyof typeof INPUT_FIELD_MODE];

export interface Campsite {
  id: number;
  code: string;
  external_id: string | null;
  campsite_type: string;
  quantity: number;
  min_persons: number | null;
  max_persons: number;
  max_pets: number;
  included_adults: number;
  included_children: number;
  included_pets: number;
  input_vehicle_info: InputFieldMode;
  input_vehicle_size: InputFieldMode;
  name: string;
  description: string;
  arrival_weekdays: number[];
  departure_weekdays: number[];
  prices: CampsitePrice[];
  images: CampsiteImage[];
  top1_attribute: TopAttribute | null;
  top2_attribute: TopAttribute | null;
  top3_attribute: TopAttribute | null;
}

export interface CampsitePrice {
  season: number;
  value: number;
}

export interface CampsiteImage {
  title: string;
  variants: {
    "1_1": { url: string };
    "4_3": { url: string };
    "4_3_big": { url: string };
    origin: { url: string; prefix: string; suffix: string };
  };
}

export interface Campground {
  slug: string;
  name: string;
  description: string;
  short_description: string;
  street1: string;
  street2: string | null;
  zip_code: string;
  city: string;
  state: string;
  country: string;
  phone: string | null;
  seasons: Season[];
  campsites: Campsite[];
  persons: Person[];
  extras: Extra[];
  logo: string | null;
  deposit_percent: number;
  minimum_fixed_deposit: string;
  accommodation_deposit_percent: number;
  accommodation_minimum_fixed_deposit: string;
  arrival_from: string;
  departure_until: string;
  book_same_day_until: string;
  book_same_day_accommodation_until: string;
  minimum_age_for_main_guest: number;
  accommodation_arrival_from: string;
  accommodation_departure_until: string;
  additional_rules: string;
  toc_url: string | null;
  privacy_policy_url: string | null;
  simple_campsite_types: boolean;
  testmode: boolean;
  languages: Language[];
  currency: string;
  base_language: string;
  cg_geo_lat: string;
  cg_geo_lng: string;
  cg_description: string;
  cg_short_description: string;
  cg_name: string;
  cg_street1: string;
  cg_street2: string | null;
  cg_country: string;
  cg_zip_code: string;
  cg_city: string;
  cg_phone: string | null;
  cg_email: string;
  pms_live_api_active: boolean;
  owner_frontend_config: unknown | null;
  additional_notification_emails: string[] | null;
  checkout_config?: { DraftBooking?: boolean };
}

export interface Currency {
  code: string;
  symbol: string;
  name: string;
}

export interface Language {
  code: string;
  name: string;
}

export interface Country {
  code: string;
  label: string;
}

export interface ApiUrls {
  price_check: string;
  get_prices: string;
  availability_check: string;
  checkout: string;
  check_booking: string;
  image: string;
  toc: string;
  privacy_policy: string;
  payload: string | null;
  prepare_booking: string;
  cancel_pending_booking: string;
  booking_details: string;
}

export interface PaymentInfos {
  rentals: {
    system: string;
    payment_provider_settings?: {
      data: {
        public_key: string;
      };
    };
  };
  pitches: {
    system: string;
    payment_provider_settings?: {
      data: {
        public_key: string;
      };
    };
  };
}

export interface AffiliateBookingWidgetAppearance {
  theme?: string;
  affiliate_logo_url?: string;
  affiliate_label?: string;
  primary_color?: string;
  background_color?: string;
  border_radius?: string;
  imprint_url?: string;
  privacy_policy_url?: string;
}

export interface CampgroundDataResponse {
  campground: Campground;
  campsite_types: [string, string][];
  countries: Record<string, string>;
  currencies: Currency[];
  languages: Language[];
  urls: ApiUrls;
  noprice_billing_types: string[];
  available: boolean;
  session_timeout: number;
  adyen_version: string | null;
  payment_infos: PaymentInfos;
  draft_booking_data?: DraftBookingData;
  affiliate_booking_widget_appearance?: AffiliateBookingWidgetAppearance;
}

export interface AvailabilityCampsite {
  id: number;
  name: string;
  campsite_type: string;
  amount: number;
  can_show_alternative_dates: boolean;
  reason: string;
  reason_txt: string;
  prices?: {
    subtotal: number;
    total: number;
    total_alt: number;
    currency: string;
  };
}

export interface AvailabilityResponse {
  campsites: AvailabilityCampsite[];
}

export interface PriceComponent {
  amount: number;
  campsite_id: number | null;
  code: string;
  date_from: string | null;
  date_until: string | null;
  days: number;
  description: string;
  external_id: string | null;
  extra_fields: ExtraFields | null;
  included: boolean;
  name: string;
  object_description: string;
  object_id: number;
  object_subtype: string | null;
  object_type: string;
  price: number;
  price_net: number;
  season_id: number | null;
  season_name: string | null;
  single_price: number;
  tax_percent: number;
}

export interface AltPriceComponent {
  object_id: number;
  price: number;
  price_net: number;
  single_price: number;
}

export interface PriceCheckResponse {
  currency: string;
  currency_symbol: string;
  currency_name: string;
  version: string;
  total: number;
  total_net: number;
  total_vat: number;
  subtotal: number;
  deposit_percent: number;
  minimum_fixed_deposit: number;
  deposit_value: number;
  payment_system: "ONLINE" | "ON_SITE" | "INVOICE";
  cancellation_terms: string[];
  components?: PriceComponent[];
  currency_alt?: {
    subtotal: number;
    total: number;
    currency: string;
    currency_symbol: string;
    currency_name: string;
    total_net: number;
    total_vat: number;
    components?: AltPriceComponent[];
  };
}

export interface DateBasedPrice {
  date_from: string;
  date_to: string;
  duration: number;
  price: number;
}

export interface SeasonBasedPrice {
  season: number;
  value: number;
}

export interface PricesCampsite {
  id: number;
  code: string | null;
  external_id: string | null;
  prices: (DateBasedPrice | SeasonBasedPrice)[];
}

export interface PersonPricePerCampsite {
  campsite: number;
  prices: DateBasedPrice[];
}

export interface PricesPerson {
  id: number;
  code: string | null;
  external_id: string | null;
  prices: SeasonBasedPrice[];
  prices_per_campsite?: PersonPricePerCampsite[];
}

export interface ExtraPricePerCampsite {
  campsite: number;
  period_price: number;
  prices: DateBasedPrice[];
}

export interface PricesExtra {
  billing_type: string;
  can_be_free: boolean;
  code: string | null;
  external_id: string | null;
  extra_sub_type: string;
  extra_type: string;
  id: number;
  price: number;
  prices_per_campsite: ExtraPricePerCampsite[];
  transit: boolean;
}

export interface PricesCampground {
  campsites: PricesCampsite[];
  extras: PricesExtra[];
  persons: PricesPerson[];
}

export interface PricesCurrencyAltPersonWithCampsite {
  id: number;
  code: string | null;
  prices: Array<{
    campsite: number;
    prices: DateBasedPrice[];
  }>;
}

export interface PricesCurrencyAltPersonWithSeason {
  id: number;
  prices: SeasonBasedPrice[];
}

export interface PricesCurrencyAlt {
  currency: string;
  currency_symbol: string;
  currency_name: string;
  campground: {
    campsites: Array<{
      id: number;
      prices: (DateBasedPrice | SeasonBasedPrice)[];
    }>;
    persons: Array<PricesCurrencyAltPersonWithCampsite | PricesCurrencyAltPersonWithSeason>;
    extras: PricesCurrencyAltExtra[];
  };
}

export interface PricesCurrencyAltExtra {
  id: number;
  code: string | null;
  prices: ExtraPriceAltPerCampsite[];
}

export interface ExtraPriceAltPerCampsite {
  campsite: number;
  prices: DateBasedPrice[];
}

export interface PricesResponse {
  campground: PricesCampground;
  currency?: string;
  currency_alt?: PricesCurrencyAlt;
  currency_name?: string;
  currency_symbol?: string;
}

export interface DraftBookingData {
  id?: string | null;
  draft_booking_id?: string;
  date_from: string;
  date_until: string;
  campsites?: Array<{ id: number; amount: number; persons: Array<{ id: number; amount: number }> }>;
  accommodation_types?: Array<{
    id: number;
    amount: number;
    accommodation_type_persons: Array<{ id: number; amount: number }>;
  }>;
  extras: Array<{ id: number; amount: number }>;
  city: string;
  country: string;
  email: string;
  first_name: string;
  last_name: string;
  message?: string;
  phone: string;
  street1: string;
  street2?: string;
  zip_code: string;
  license_plate?: string;
  vehicle_length?: number;
  vehicle_width?: number;
  guests: Guest[];
  draft_booking_link?: string;
}

export interface CheckoutResponse {
  booking_id: string;
  booking_number: string;
  payment_url?: string;
  payment_methods?: string[];
  created?: Date;
  draft_booking_link?: string;
  draft_booking?: DraftBookingData;
}

export interface CheckoutErrorResponse {
  non_field_errors?: string[];
}

export interface BookingDetails {
  booking_id: string;
  booking_number: string;
  booking_status: "confirmed" | "pending" | "internally_canceled" | "canceled";
  created_at: string;
  booking_data: {
    date_from: string;
    date_until: string;
    campsites: Array<{
      id: number;
      name: string;
      amount: number;
      persons: Array<{
        id: number;
        name: string;
        amount: number;
        person_type: string;
      }>;
    }>;
    extras: Array<{
      id: number;
      amount: number;
    }>;
  };
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  street1: string;
  street2?: string;
  zip_code: string;
  city: string;
  country: string;
  message?: string;
  license_plate?: string;
  vehicle_length?: number;
  vehicle_width?: number;
  guests: Guest[];
  num_persons: number;
  pricelist: PriceCheckResponse;
  aid?: string;
}

export interface Guest {
  first_name: string;
  last_name: string;
  birthdate: string;
}

export interface Attribute {
  id: number;
  uuid: string;
  active: boolean;
  valid_for_rentals: boolean;
  valid_for_pitches: boolean;
  positive_values: boolean;
  name: string;
  description: string;
}

export interface AttributeGroup {
  id: number;
  uuid: string;
  active: boolean;
  name: string;
  description: string;
  attributes: Attribute[];
}

export interface AccommodationTypeAttributes {
  id: number;
  attributes: AttributeGroup[];
}

export interface CampsiteData {
  basicCampsite: Campsite;
  availabilityCampsite: AvailabilityCampsite;
  attributes: AttributeGroup[];
}

export interface StripePaymentIntentResponse {
  clientSecret: string;
  stripeAccountId: string;
  error?: {
    message: string;
  };
}

export interface StripePaymentStatusResponse {
  status: "SUCCESS" | "FAILED" | "PENDING";
}

export interface AccommodationDayInfo {
  stock: number;
  open: boolean;
  can_arrive: boolean;
  can_departure: boolean;
  min_nights: number | null;
  max_nights: number | null;
  lead_days: number | null;
}

export interface AccommodationDayInfoResponse {
  date_from: string;
  date_to: string;
  with_base_price: boolean;
  accommodation_type: {
    id: number;
    code: string;
    external_id: string | null;
    campsite_type: string;
    quantity: number;
    min_persons: number | null;
    max_persons: number;
    max_pets: number;
    included_adults: number;
    included_children: number;
    included_pets: number;
    name: string;
    description: string;
  };
  day_list: Record<string, AccommodationDayInfo>;
}
